import type { SessionInfo } from './types';

/** Cross-platform base64 → string. Browsers + RN both have atob. */
function decodeBase64Url(b64: string): string {
  // JWT uses base64url; normalize to base64.
  const normalized = b64.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '==='.slice((normalized.length + 3) % 4);
  // atob is global on web and on RN (polyfilled by Hermes/quick-crypto).
  return atob(padded);
}

export function decodeJwtPayload(
  token: string,
): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return null;
    return JSON.parse(decodeBase64Url(parts[1]));
  } catch {
    return null;
  }
}

/**
 * Extract session info (userId, isGuest, claim-derived profile) from a JWT.
 * Returns nulls if the token is missing or undecodable; never throws.
 */
export function sessionInfoFromToken(
  token: string | null,
  expiresAt: number | null,
): SessionInfo {
  if (!token) {
    return {
      token: null,
      expiresAt: null,
      isGuest: false,
      userId: null,
      email: null,
      firstName: null,
      lastName: null,
      avatarUrl: null,
    };
  }
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return {
      token,
      expiresAt,
      isGuest: false,
      userId: null,
      email: null,
      firstName: null,
      lastName: null,
      avatarUrl: null,
    };
  }
  const appMeta = (payload['app_metadata'] as Record<string, unknown>) ?? {};
  const userMeta = (payload['user_metadata'] as Record<string, unknown>) ?? {};
  const isGuest =
    appMeta['provider'] === 'guest' || userMeta['is_guest'] === true;
  const fullName =
    (userMeta['full_name'] as string | undefined) ||
    (userMeta['name'] as string | undefined);
  const splitName = fullName ? fullName.trim().split(/\s+/) : [];
  return {
    token,
    expiresAt,
    isGuest,
    userId: (payload['sub'] as string) || null,
    email:
      (payload['email'] as string) || (userMeta['email'] as string) || null,
    firstName:
      (userMeta['first_name'] as string) ||
      (userMeta['given_name'] as string) ||
      splitName[0] ||
      null,
    lastName:
      (userMeta['last_name'] as string) ||
      (userMeta['family_name'] as string) ||
      (splitName.length > 1 ? splitName.slice(1).join(' ') : null) ||
      null,
    avatarUrl:
      (userMeta['avatar_url'] as string) ||
      (userMeta['picture'] as string) ||
      null,
  };
}
